/**
 * RecruitCharacterModal component - modal for recruiting additional characters.
 * Similar to CreateCharacterModal but for non-primary characters.
 * Requires Camp Hall building and shows XP bonus info.
 */

import { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import type { CharacterClassWithRanges } from '@/types/characters';
import { ClassSelector } from './ClassSelector';

interface RecruitmentCapacity {
    total: number;
    base: number;
    from_level: number;
    from_assigned: number;
    camp_hall_level: number;
    camp_hall_stars: number;
    assigned_count: number;
    has_camp_hall: boolean;
}

interface RecruitmentInfo {
    capacity: RecruitmentCapacity;
    current_count: number;
    available_slots: number;
    can_recruit: boolean;
    xp_bonus: number;
    reason: string | null;
}

interface RecruitCharacterModalProps {
    /** The hex ID where the character will be created */
    hexId: number;
    /** Whether the modal is open */
    isOpen: boolean;
    /** Recruitment info from backend */
    recruitmentInfo: RecruitmentInfo | null;
    /** Callback to close the modal */
    onClose: () => void;
    /** Callback when character is created successfully */
    onCreated: () => void;
}

/**
 * Modal for recruiting additional characters (non-primary).
 * Requires a Camp Hall building and shows XP bonus based on stars.
 */
export function RecruitCharacterModal({
    hexId,
    isOpen,
    recruitmentInfo,
    onClose,
    onCreated,
}: RecruitCharacterModalProps) {
    const [classes, setClasses] = useState<CharacterClassWithRanges[]>([]);
    const [statLabels, setStatLabels] = useState<Record<string, string>>({});
    const [skillLabels, setSkillLabels] = useState<Record<string, string>>({});
    const [selectedClass, setSelectedClass] = useState<string | null>(null);
    const [characterName, setCharacterName] = useState('');
    const [pronouns, setPronouns] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch available classes when modal opens
    useEffect(() => {
        if (!isOpen) return;

        const fetchClasses = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`/hex/${hexId}/characters/classes`);
                if (!response.ok) throw new Error('Failed to fetch classes');
                const data = await response.json();
                setClasses(data.classes);
                setStatLabels(data.statLabels);
                setSkillLabels(data.skillLabels);
            } catch (err) {
                setError('Failed to load character classes');
                console.error('Failed to fetch classes:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchClasses();
    }, [isOpen, hexId]);

    // Handle character recruitment
    const handleRecruit = () => {
        if (!selectedClass || !characterName.trim() || isCreating) return;

        setIsCreating(true);
        setError(null);

        router.post(
            `/hex/${hexId}/characters/recruit`,
            {
                name: characterName.trim(),
                pronouns: pronouns.trim() || null,
                class: selectedClass,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    onCreated();
                    onClose();
                    // Reset form
                    setSelectedClass(null);
                    setCharacterName('');
                    setPronouns('');
                },
                onError: (errors) => {
                    setError(Object.values(errors).flat().join(', '));
                },
                onFinish: () => {
                    setIsCreating(false);
                },
            }
        );
    };

    if (!isOpen) return null;

    const xpBonus = recruitmentInfo?.xp_bonus || 0;
    const campHallStars = recruitmentInfo?.capacity.camp_hall_stars || 0;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden border border-slate-700 flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-700">
                    <h2 className="text-xl font-bold text-white">Recruit New Character</h2>
                    <p className="text-sm text-gray-400 mt-1">
                        Recruit an additional character to join your party. Choose their class and name wisely!
                    </p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {isLoading ? (
                        <div className="text-center text-gray-400 py-8">
                            Loading character classes...
                        </div>
                    ) : error && classes.length === 0 ? (
                        <div className="text-center text-red-400 py-8">{error}</div>
                    ) : (
                        <div className="space-y-6">
                            {/* XP Bonus Info */}
                            {xpBonus > 0 && (
                                <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl">🏕️</span>
                                        <div>
                                            <h4 className="text-amber-300 font-semibold">Camp Hall Bonus</h4>
                                            <p className="text-amber-100/70 text-sm">
                                                Your {'★'.repeat(campHallStars)}{'☆'.repeat(5 - campHallStars)} Camp Hall grants
                                                <span className="font-bold text-amber-300"> +{xpBonus} XP</span> to newly recruited characters!
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Character Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Character Name
                                </label>
                                <input
                                    type="text"
                                    value={characterName}
                                    onChange={(e) => setCharacterName(e.target.value)}
                                    placeholder="Enter your character's name..."
                                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                                    maxLength={100}
                                />
                            </div>

                            {/* Pronouns */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Pronouns <span className="text-gray-500">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={pronouns}
                                    onChange={(e) => setPronouns(e.target.value)}
                                    placeholder="e.g., he/him, she/her, they/them..."
                                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                                    maxLength={50}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Used when describing this character's actions in adventures.
                                </p>
                            </div>

                            {/* Class Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Choose Character Class
                                </label>
                                <ClassSelector
                                    classes={classes}
                                    selectedClass={selectedClass}
                                    onSelect={setSelectedClass}
                                    statLabels={statLabels}
                                    skillLabels={skillLabels}
                                />
                            </div>

                            {/* Error display */}
                            {error && (
                                <div className="p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300 text-sm">
                                    {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isCreating}
                        className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleRecruit}
                        disabled={!selectedClass || !characterName.trim() || isCreating || isLoading}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                        {isCreating ? 'Recruiting...' : `Recruit Character${xpBonus > 0 ? ` (+${xpBonus} XP)` : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default RecruitCharacterModal;
